@layer refine.base {
  :host {
    --_bg: var(--alert-bg, var(--_theme-backdrop));
    --_color: var(--alert-color, var(--_theme-base));
    --_border-color: var(--alert-border-color, var(--_theme-border));
    --_radius: var(--alert-radius, var(--rounded-lg));
    --_padding: var(--alert-padding, var(--size-4));
    --_gap: var(--alert-gap, var(--size-3));
    --_font-size: var(--alert-font-size, var(--text-sm));
    --_shadow: var(--alert-shadow, var(--shadow-none));

    display: block;
  }

  .sr-announce {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    white-space: nowrap;
    border-width: 0;
    clip-path: inset(50%);
  }

  :host([dismissed]) {
    display: none;
  }

  :host([dismissing]) {
    overflow: hidden;
    pointer-events: none;
    animation: var(--_motion-animation, ore-alert-exit var(--transition-slow) var(--ease-in-out) forwards);
  }

  @keyframes ore-alert-exit {
    0% {
      /* Use a large enough value to cover any realistic alert height. */
      max-height: 600px;
      margin-bottom: var(--size-3);
      opacity: 1;
      transform: scaleY(1);
    }

    60% {
      opacity: 0;
      transform: scaleY(0.8);
    }

    100% {
      max-height: 0;
      padding-top: 0;
      padding-bottom: 0;
      margin-bottom: 0;
      opacity: 0;
      transform: scaleY(0);
    }
  }

  .alert {
    display: grid;
    grid-template-columns: auto 1fr auto;
    row-gap: var(--size-1);
    padding: var(--_padding);
    overflow: hidden;
    font-size: var(--_font-size);
    line-height: var(--leading-normal);
    color: var(--_color);
    background: var(--_bg);
    border: var(--border) solid var(--_border-color);
    border-radius: var(--_radius);
    box-shadow: var(--_shadow);
    transition:
      background var(--transition-normal),
      border-color var(--transition-normal),
      box-shadow var(--transition-normal),
      color var(--transition-normal);
  }

  /* ── Icon ──────────────────────────────────────── */

  .icon {
    display: inline-flex;
    grid-row: 1;
    grid-column: 1;
    align-items: center;
    align-self: start;
    justify-content: center;
    margin-inline-end: var(--_gap);
  }

  /* Without heading: icon aligns with the body content */
  .alert:has(.header[hidden]) .icon {
    align-self: center;
  }

  .icon[hidden] {
    display: none;
  }

  /* ── Header row (heading + meta) ───────────────── */

  .header {
    display: flex;
    grid-row: 1;
    grid-column: 2;
    gap: var(--size-2);
    align-items: baseline;
  }

  .header[hidden] {
    display: none;
  }

  .heading {
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: var(--font-semibold);
    line-height: var(--leading-normal);
    white-space: nowrap;
  }

  .meta {
    margin-inline-start: auto;
    font-size: var(--text-xs);
    white-space: nowrap;
    opacity: 0.7;
  }

  :host(:not(:has([slot='meta']))) .meta {
    display: none;
  }

  /* ── Body ──────────────────────────────────────── */

  .body {
    grid-row: 2;
    grid-column: 1 / -1;
    min-width: 0;
    margin-top: var(--size-1);
  }

  /* Without heading: body moves up to share row with icon and close */
  .alert:has(.header[hidden]) .body {
    grid-row: 1;
    grid-column: 2;
    margin-top: 0;
  }

  /* ── Content ───────────────────────────────────── */

  .content {
    line-height: var(--leading-relaxed);
  }

  /* ── Actions ───────────────────────────────────── */

  .actions {
    display: flex;
    flex-wrap: wrap;
    grid-row: 3;
    grid-column: 1 / -1;
    gap: var(--size-2);
    margin-top: var(--size-2);
  }

  /* Without heading: actions move to row 2 */
  .alert:has(.header[hidden]) .actions {
    grid-row: 2;
  }

  .actions[hidden] {
    display: none;
  }

  /* Horizontal mode: 4-column grid — icon | body | actions | close */
  :host([horizontal]) .alert {
    grid-template-columns: auto 1fr auto auto;
  }

  :host([horizontal]) .body {
    grid-row: 1;
    grid-column: 2;
    align-self: center;
    margin-top: 0;
  }

  :host([horizontal]) .actions:not([hidden]) {
    flex-wrap: nowrap;
    grid-row: 1;
    grid-column: 3;
    margin-inline-start: var(--_gap);
    margin-top: 0;
  }

  :host([horizontal]) .close {
    grid-column: 4;
    align-self: center;
  }

  /* ── Close button ──────────────────────────────── */

  .close {
    display: inline-flex;
    grid-row: 1;
    grid-column: 3;
    align-items: center;
    align-self: start;
    justify-content: center;
    width: var(--size-5);
    min-width: var(--_touch-target);
    height: var(--size-5);
    min-height: var(--_touch-target);
    padding: 0;
    margin-inline-start: var(--_gap);
    color: inherit;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: var(--rounded-sm);
    opacity: 0.7;
    transition:
      opacity var(--transition-fast),
      background var(--transition-fast);
  }

  .close[hidden] {
    display: none;
  }

  .close:hover {
    background: color-mix(in oklch, currentcolor 12%, transparent);
    opacity: 1;
  }

  .close:focus-visible {
    outline: var(--border-2) solid currentcolor;
    outline-offset: var(--border-2);
    opacity: 1;
  }

  /* Accented left border for flat and bordered variants */
  :host([variant='flat'][accented]) .alert,
  :host([variant='bordered'][accented]) .alert {
    border-inline-start-width: var(--border-4, 4px);
  }
}

@layer refine.variants {
  /* Flat (Default) — canvas background, theme-colored border and text */
  :host(:not([variant])),
  :host([variant='flat']) {
    --_bg: var(--alert-bg, var(--color-canvas));
    --_color: var(--alert-color, var(--_theme-base));
    --_border-color: var(--alert-border-color, var(--_theme-base));
    --_shadow: var(--alert-shadow, var(--shadow-none));
  }

  /* Solid — fully filled with theme color */
  :host([variant='solid']) {
    --_bg: var(--alert-bg, var(--_theme-base));
    --_color: var(--alert-color, var(--_theme-contrast));
    --_border-color: var(--alert-border-color, var(--_theme-base));
    --_shadow: var(--alert-shadow, var(--shadow-xs));
  }

  /* Bordered — tinted background with a subtle themed border */
  :host([variant='bordered']) {
    --_bg: var(--alert-bg, color-mix(in oklch, var(--_theme-backdrop), var(--color-canvas)));
    --_color: var(--alert-color, var(--_theme-base));
    --_border-color: var(--alert-border-color, color-mix(in oklch, var(--_theme-focus) 30%, transparent));
    --_shadow: var(--alert-shadow, var(--inset-shadow-2xs));
  }
}
